import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ArrowRight, Brain, Router, TrendingUp, Activity } from 'lucide-react'

interface SignalFlowStep {
  name: string
  icon: React.ReactNode
  status: 'active' | 'processing' | 'success' | 'error' | 'inactive'
  description: string
  lastActivity?: string
}

interface SignalFlowDiagramProps {
  botName: string
  patternProcessingEnabled: boolean
  recentActivity?: {
    tradingView: string
    patternAnalysis: string
    signalProcessor: string
    signalRouter: string
  }
}

export const SignalFlowDiagram: React.FC<SignalFlowDiagramProps> = ({
  botName,
  patternProcessingEnabled,
  recentActivity
}) => {
  const steps: SignalFlowStep[] = [
    {
      name: 'TradingView',
      icon: <TrendingUp className="h-4 w-4" />,
      status: 'active',
      description: 'Market signals from charts',
      lastActivity: recentActivity?.tradingView
    },
    {
      name: 'Pattern Analysis',
      icon: <Activity className="h-4 w-4" />,
      status: 'active',
      description: 'AI pattern recognition',
      lastActivity: recentActivity?.patternAnalysis
    },
    {
      name: 'Signal Processor',
      icon: <Brain className="h-4 w-4" />,
      status: patternProcessingEnabled ? 'active' : 'inactive',
      description: 'AI decision processing',
      lastActivity: recentActivity?.signalProcessor
    },
    {
      name: 'Signal Router',
      icon: <Router className="h-4 w-4" />,
      status: 'active',
      description: 'Execute trading signals',
      lastActivity: recentActivity?.signalRouter
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500'
      case 'processing':
        return 'bg-yellow-500 animate-pulse'
      case 'success':
        return 'bg-blue-500'
      case 'error':
        return 'bg-red-500'
      case 'inactive':
        return 'bg-muted'
      default:
        return 'bg-muted'
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>
      case 'processing':
        return <Badge variant="secondary">Processing</Badge>
      case 'success':
        return <Badge variant="default">Success</Badge>
      case 'error':
        return <Badge variant="destructive">Error</Badge>
      case 'inactive':
        return <Badge variant="secondary">Disabled</Badge>
      default:
        return <Badge variant="secondary">Unknown</Badge>
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Signal Flow for {botName}</CardTitle>
        <CardDescription>
          Real-time signal processing pipeline
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.name} className="flex items-center">
                <div className="flex flex-col items-center space-y-2 flex-1">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getStatusColor(step.status)}`}>
                    {step.icon}
                  </div>
                  <div className="text-center">
                    <h4 className="text-sm font-medium">{step.name}</h4>
                    <p className="text-xs text-muted-foreground">{step.description}</p>
                    {getStatusBadge(step.status)}
                    {step.lastActivity && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last: {new Date(step.lastActivity).toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                </div>
                
                {index < steps.length - 1 && (
                  <ArrowRight className="h-6 w-6 text-muted-foreground mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>

        {!patternProcessingEnabled && (
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <Brain className="h-4 w-4 inline mr-1" />
              Pattern processing is disabled. Signals flow directly from Pattern Analysis to Signal Router.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}